import Button from '../Button';
import React from 'react';
import {
  EOrderProductKind,
  IOrderLine,
  IOrderProduct,
  IMenuBottomNavProps,
  IOrderGroupProduct,
} from '@orderingstack/ordering-types';
import { animated, useTrail, useTransition } from 'react-spring';
import { formatPrice, isCartEmpty } from '../../utils';
import { useTranslation } from 'react-i18next';
import {
  getGroupedBucketLines,
  getMediaUrl,
  summarizeLineDiscounts,
} from '@orderingstack/front-hooks';
import { ReactComponent as Trash } from '../../icons/trash.svg';
import { ReactComponent as Minus } from '../../icons/minus.svg';
import { ReactComponent as Plus } from '../../icons/plus.svg';

function getProductNames(
  product: IOrderProduct | IOrderGroupProduct
): string[] {
  if (
    product.kind !== EOrderProductKind.PRODUCT &&
    product.items &&
    product.items.length > 0
  ) {
    return product.items.flatMap(getProductNames);
  }
  return [product.literals?.name];
}

const lineTotal = (line: IOrderLine) =>
  line.quantity * parseFloat(line.price) - summarizeLineDiscounts(line);

export default function BottomNav({
  goToCheckout,
  order,
  currency,
  fallbackImg,
  changeQuantity,
}: IMenuBottomNavProps) {
  const { t } = useTranslation();
  const transitions = useTransition(null, {
    from: {
      transform: `translate3d(0,100%,0)`,
    },
    enter: {
      transform: 'translate3d(0,0%,0)',
    },
    leave: {
      transform: `translate3d(0,-100%,0)`,
    },
    config: {
      friction: 20,
    },
  });

  const groupedLines: IOrderLine[] =
    getGroupedBucketLines(order?.buckets) || [];

  const trail = useTrail(groupedLines.length, {
    opacity: 1,
    x: 0,
    zIndex: 10,
    width: 234,
    from: { opacity: 0, x: -234, width: 0, zIndex: -1000 },
    config: { mass: 5, tension: 2000, friction: 200 },
  });

  return transitions((style) => (
    <animated.div
      style={style}
      className="custom-shadow-xl z-20 flex h-[236px] w-full flex-row items-center justify-between overflow-hidden"
    >
      {isCartEmpty(order) && (
        <div className="ml-8 grow text-2xl text-fontColor-btnDisabled">
          {t('3e.kiosk.menu.bottomNav.cartIsEmpty', {
            defaultValue: 'Twój koszyk jest pusty',
          })}
        </div>
      )}
      {!isCartEmpty(order) && (
        <div className="relative h-full grow overflow-hidden">
          <div className="flex h-full grow flex-row overflow-x-auto">
            {trail.map(({ x, width, ...rest }, index) => {
              const line = groupedLines[index];
              return (
                <animated.div
                  className="border-borderColor flex shrink-0 flex-col items-center justify-between border-r-2 bg-bgColor"
                  key={line.id}
                  style={{
                    width,
                    ...rest,
                    transform: x.interpolate((x) => `translate3d(${x}px,0,0)`),
                  }}
                >
                  <div className="flex w-full flex-col items-center">
                    <img
                      className="size-20"
                      alt="img"
                      src={getMediaUrl(
                        line.product?.img || fallbackImg || '',
                        160
                      )}
                    />
                    <div className="w-full truncate px-2 text-center text-xl">
                      {line.product?.literals?.name}
                    </div>
                    <div className="font-cartProductItems text-fontColor-cartProductItems w-full truncate px-2 text-center">
                      {line.product?.items?.flatMap(getProductNames).join(', ')}
                    </div>
                  </div>
                  <div className="font-price grow text-lg">
                    {formatPrice(lineTotal(line), currency)}
                  </div>
                  <div className="mb-6 mt-3 flex h-10 shrink-0 flex-row items-center justify-center px-5">
                    <button
                      className="bg-bgColor-btnTrash mr-6 flex aspect-square w-10 flex-row items-center justify-center rounded-full"
                      onClick={() => changeQuantity(line, 0)}
                    >
                      <Trash fill="var(--font-color-btn-trash)" />
                    </button>
                    <button
                      className="flex aspect-square w-10 flex-row items-center justify-center rounded-full bg-bgColor-btnSecondary"
                      onClick={() => changeQuantity(line, line.quantity - 1)}
                    >
                      <Minus fill="var(--font-primary)" />
                    </button>
                    <div className="font-price mx-1 w-fit min-w-[24px] text-center">
                      {line.quantity}
                    </div>
                    <button
                      className="flex aspect-square w-10 flex-row items-center justify-center rounded-full bg-bgColor-btnSecondary"
                      onClick={() => changeQuantity(line, line.quantity + 1)}
                    >
                      <Plus fill="var(--font-primary)" />
                    </button>
                  </div>
                </animated.div>
              );
            })}
          </div>
          <div className="absolute right-[-4px] top-0 z-20 h-full w-[56px] [background:linear-gradient(-90deg,_var(--color-background),_rgba(255,_255,_255,_0))]" />
        </div>
      )}
      <div className="bg-bgColor px-6">
        <Button
          onClick={() => goToCheckout?.({ showUpsell: true })}
          disabled={isCartEmpty(order)}
          className={`h-[120px] shrink-0`}
        >
          <div className="whitespace-nowrap">
            {t('3e.kiosk.menu.bottomNav.placeOrder', {
              defaultValue: 'Złóż zamówienie',
            })}
          </div>
          <div className="font-price">
            &nbsp;{formatPrice(order?.editTotal || '0', currency)}
          </div>
        </Button>
      </div>
    </animated.div>
  ));
}
